import { Fragment, type ReactNode } from 'react';
import { SmartLink } from '../ui/SmartLink';

// ---------------------------------------------------------------------------
// Knowledge Base markdown renderer (CLE-265).
//
// A dependency-free renderer tuned for the KB document viewer. Compared to the
// minimal chat renderer it adds: stable heading ids (for the auto-generated
// table of contents + anchor links), GitHub-style tables, blockquotes and
// lightly syntax-highlighted fenced code blocks.
//
// Safety: never uses dangerouslySetInnerHTML — all text is rendered as React
// children so document content cannot inject HTML.
// ---------------------------------------------------------------------------

export interface TocEntry {
  id: string;
  text: string;
  level: number;
}

// --- Heading id (slug) -----------------------------------------------------

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // strip diacritics
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

// --- Inline formatting -----------------------------------------------------

const INLINE_RE = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const parts = text.split(INLINE_RE);
  return parts.map((part, i) => {
    const key = `${keyPrefix}-${i}`;
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={key} className="font-semibold text-foreground">
          {part.slice(2, -2)}
        </strong>
      );
    }
    if (part.startsWith('*') && part.endsWith('*')) {
      return (
        <em key={key} className="italic">
          {part.slice(1, -1)}
        </em>
      );
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <code
          key={key}
          className="rounded bg-muted px-1.5 py-0.5 font-mono text-[0.85em] text-brand-600"
        >
          {part.slice(1, -1)}
        </code>
      );
    }
    const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(part);
    if (link) {
      return (
        <SmartLink
          key={key}
          href={link[2]}
          className="text-brand-600 underline underline-offset-2 hover:opacity-80"
        >
          {link[1]}
        </SmartLink>
      );
    }
    return <Fragment key={key}>{part}</Fragment>;
  });
}

// --- Lightweight code highlighting -----------------------------------------
// We don't pull a full syntax highlighter (keeps the bundle lean). Instead we
// colour a handful of universal tokens — comments, strings, numbers and a set
// of common keywords — which reads well across the languages used in the KB
// (bash, ts, json, sql, text).

const KEYWORDS = new Set([
  'function',
  'const',
  'let',
  'var',
  'return',
  'if',
  'else',
  'for',
  'while',
  'throw',
  'new',
  'import',
  'export',
  'from',
  'class',
  'interface',
  'type',
  'async',
  'await',
  'true',
  'false',
  'null',
  'undefined',
  'SELECT',
  'FROM',
  'WHERE',
  'CREATE',
  'INDEX',
  'TABLE',
  'ALTER',
  'ADD',
  'CONSTRAINT',
  'UNIQUE',
  'ON',
  'docker',
  'git',
  'npm',
  'cp',
  'cd',
]);

function highlightToken(token: string, key: string): ReactNode {
  if (/^\d+(\.\d+)?$/.test(token)) {
    return (
      <span key={key} className="text-amber-300">
        {token}
      </span>
    );
  }
  if (KEYWORDS.has(token)) {
    return (
      <span key={key} className="text-brand-400 font-medium">
        {token}
      </span>
    );
  }
  return <Fragment key={key}>{token}</Fragment>;
}

function highlightLine(line: string, key: string): ReactNode {
  // Whole-line comment.
  const trimmed = line.trimStart();
  if (trimmed.startsWith('#') || trimmed.startsWith('//') || trimmed.startsWith('--')) {
    return (
      <span key={key} className="text-slate-500 italic">
        {line}
      </span>
    );
  }
  // Split on string literals first so we don't tokenise inside them.
  const segments = line.split(/("[^"]*"|'[^']*')/);
  return (
    <Fragment key={key}>
      {segments.map((seg, si) => {
        if (
          (seg.startsWith('"') && seg.endsWith('"')) ||
          (seg.startsWith("'") && seg.endsWith("'"))
        ) {
          return (
            <span key={`${key}-s${si}`} className="text-emerald-300">
              {seg}
            </span>
          );
        }
        // Tokenise the non-string segment on word boundaries.
        const tokens = seg.split(/(\b)/);
        return tokens.map((tok, ti) => highlightToken(tok, `${key}-${si}-${ti}`));
      })}
    </Fragment>
  );
}

// --- Block parsing ---------------------------------------------------------

type Block =
  | { kind: 'heading'; level: number; text: string; id: string }
  | { kind: 'code'; lang: string; text: string }
  | { kind: 'ul'; items: string[] }
  | { kind: 'ol'; items: string[] }
  | { kind: 'quote'; text: string }
  | { kind: 'table'; header: string[]; rows: string[][] }
  | { kind: 'p'; text: string };

function splitRow(line: string): string[] {
  return line
    .trim()
    .replace(/^\||\|$/g, '')
    .split('|')
    .map((c) => c.trim());
}

function isTableSeparator(line: string): boolean {
  return /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|?\s*$/.test(line);
}

export function parseMarkdown(content: string): { blocks: Block[]; toc: TocEntry[] } {
  const lines = content.replace(/\r\n/g, '\n').split('\n');
  const blocks: Block[] = [];
  const toc: TocEntry[] = [];
  const usedIds = new Set<string>();
  let i = 0;

  const makeId = (text: string): string => {
    const base = slugify(text) || 'section';
    let id = base;
    let n = 2;
    while (usedIds.has(id)) {
      id = `${base}-${n}`;
      n += 1;
    }
    usedIds.add(id);
    return id;
  };

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block (optionally with a language hint).
    if (line.trimStart().startsWith('```')) {
      const lang = line.trimStart().slice(3).trim();
      const codeLines: string[] = [];
      i += 1;
      while (i < lines.length && !lines[i].trimStart().startsWith('```')) {
        codeLines.push(lines[i]);
        i += 1;
      }
      i += 1; // skip closing fence
      blocks.push({ kind: 'code', lang, text: codeLines.join('\n') });
      continue;
    }

    // Heading.
    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    if (heading) {
      const level = heading[1].length;
      const text = heading[2].trim();
      const id = makeId(text);
      blocks.push({ kind: 'heading', level, text, id });
      // h2/h3 feed the table of contents (h1 is the doc title).
      if (level >= 2 && level <= 3) toc.push({ id, text, level });
      i += 1;
      continue;
    }

    // Table: header row followed by a separator row.
    if (line.includes('|') && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
      const header = splitRow(line);
      const rows: string[][] = [];
      i += 2; // skip header + separator
      while (i < lines.length && lines[i].includes('|') && lines[i].trim() !== '') {
        rows.push(splitRow(lines[i]));
        i += 1;
      }
      blocks.push({ kind: 'table', header, rows });
      continue;
    }

    // Blockquote.
    if (/^\s*>\s?/.test(line)) {
      const quoteLines: string[] = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) {
        quoteLines.push(lines[i].replace(/^\s*>\s?/, ''));
        i += 1;
      }
      blocks.push({ kind: 'quote', text: quoteLines.join(' ') });
      continue;
    }

    // Unordered list.
    if (/^\s*[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ''));
        i += 1;
      }
      blocks.push({ kind: 'ul', items });
      continue;
    }

    // Ordered list.
    if (/^\s*\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ''));
        i += 1;
      }
      blocks.push({ kind: 'ol', items });
      continue;
    }

    // Blank line.
    if (line.trim() === '') {
      i += 1;
      continue;
    }

    // Paragraph: gather consecutive plain lines.
    const paraLines: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !lines[i].trimStart().startsWith('```') &&
      !/^(#{1,4})\s+/.test(lines[i]) &&
      !/^\s*>\s?/.test(lines[i]) &&
      !/^\s*[-*]\s+/.test(lines[i]) &&
      !/^\s*\d+\.\s+/.test(lines[i]) &&
      !lines[i].includes('|')
    ) {
      paraLines.push(lines[i]);
      i += 1;
    }
    if (paraLines.length) blocks.push({ kind: 'p', text: paraLines.join(' ') });
    else i += 1; // a lone "|" line we didn't treat as a table — skip
  }

  return { blocks, toc };
}

// --- Render ----------------------------------------------------------------

const HEADING_STYLES: Record<number, string> = {
  1: 'text-2xl font-bold text-foreground mt-2 mb-4',
  2: 'text-xl font-semibold text-foreground mt-8 mb-3 scroll-mt-24',
  3: 'text-lg font-semibold text-foreground mt-6 mb-2 scroll-mt-24',
  4: 'text-base font-semibold text-foreground mt-4 mb-2 scroll-mt-24',
};

export function KbMarkdown({ content }: { content: string }) {
  const { blocks } = parseMarkdown(content);

  return (
    <div className="text-[0.95rem] leading-relaxed text-muted-foreground">
      {blocks.map((block, idx) => {
        const key = `b-${idx}`;
        switch (block.kind) {
          case 'heading': {
            const cls = HEADING_STYLES[block.level] ?? HEADING_STYLES[4];
            const Tag = `h${block.level}` as 'h1' | 'h2' | 'h3' | 'h4';
            return (
              <Tag key={key} id={block.id} className={cls}>
                {renderInline(block.text, key)}
              </Tag>
            );
          }
          case 'code':
            return (
              <pre
                key={key}
                className="my-4 overflow-x-auto rounded-lg border border-border bg-gray-50 p-4 font-mono text-[0.8rem] leading-relaxed text-gray-800"
              >
                <code>
                  {block.text.split('\n').map((ln, li) => (
                    <Fragment key={`${key}-l${li}`}>
                      {highlightLine(ln, `${key}-l${li}`)}
                      {'\n'}
                    </Fragment>
                  ))}
                </code>
              </pre>
            );
          case 'ul':
            return (
              <ul key={key} className="my-3 list-disc space-y-1.5 pl-6 marker:text-brand-500">
                {block.items.map((it, j) => (
                  <li key={`${key}-${j}`}>{renderInline(it, `${key}-${j}`)}</li>
                ))}
              </ul>
            );
          case 'ol':
            return (
              <ol key={key} className="my-3 list-decimal space-y-1.5 pl-6 marker:text-brand-500">
                {block.items.map((it, j) => (
                  <li key={`${key}-${j}`}>{renderInline(it, `${key}-${j}`)}</li>
                ))}
              </ol>
            );
          case 'quote':
            return (
              <blockquote
                key={key}
                className="my-4 rounded-r-md border-l-4 border-brand-500 bg-brand-50 px-4 py-2 text-foreground"
              >
                {renderInline(block.text, key)}
              </blockquote>
            );
          case 'table':
            return (
              <div key={key} className="my-4 overflow-x-auto rounded-lg border border-border">
                <table className="w-full border-collapse text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      {block.header.map((h, hi) => (
                        <th
                          key={`${key}-h${hi}`}
                          className="border-b border-border px-3 py-2 text-left font-semibold text-foreground"
                        >
                          {renderInline(h, `${key}-h${hi}`)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {block.rows.map((row, ri) => (
                      <tr key={`${key}-r${ri}`} className="even:bg-muted/20">
                        {row.map((cell, ci) => (
                          <td
                            key={`${key}-r${ri}-c${ci}`}
                            className="border-b border-border/60 px-3 py-2 align-top"
                          >
                            {renderInline(cell, `${key}-r${ri}-c${ci}`)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          default:
            return (
              <p key={key} className="my-3">
                {renderInline(block.text, key)}
              </p>
            );
        }
      })}
    </div>
  );
}
